import { useState, useRef, type ReactNode } from "react";
import { Col, Row, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import NoImage from "../../assets/images/utility/no_image.png";
import { getURLPrefix } from "../../utils/getUrlPrefix";
import PricingPlanCard from "./PricingPlanCard";
import BulkActionsTable, { type ColumnSpec } from "../merchandising/components/tables/BulkActionsTable";
import { getPlanType, getPlanTypeLabel, getTemplateTypeLabel, SEGMENT_TEXT, ForecastBadge } from "./utils";
import { formatCurrency, formatPercent, formatNumber } from "../../utils/formatNumber";
import TrellisLink from "../../components/core/basic/TrellisLink";
import useCustomTableFilters from "../../hooks/useCustomTableFilters";
import moment, { type MomentInput } from "moment";
import { usePricingLifecycleFilters } from "./pricingLifecycleFilters";
import { trellisPalette } from "../../components/custom/analytics/palettes";
import { useModal } from "@ebay/nice-modal-react";
import { QuickPlanConfigModal } from "./components/modals/QuickPlanConfig";
import { Button } from "../../components/core/basic/Button";
import { MdEdit } from "react-icons/md";
import { useAppSelector } from "../../redux/hooks";
import { useBulkUpdatePricingPlanStatus } from "./api/useBulkUpdatePricingPlanStatus";
import { FaExclamationTriangle } from "react-icons/fa";
import { useModuleEnabled } from "../../redux/selectors/modules";
import { useSimpleCan } from "../../config/casl/casl";
import { MetricLabel } from "../../components/i18n/MetricLabel";
import type { TFunction } from "i18next";

const URL_PREFIX = getURLPrefix();

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type TranslateFn = TFunction<any, any>;

const translateSegmentText = (t: TranslateFn, label: string | undefined): string => {
  switch (label) {
    case "Sales Pressure":
      return t(($) => $.pricingv2["Sales Pressure"]);
    case "Profit Potential":
      return t(($) => $.pricingv2["Profit Potential"]);
    case "Right Price":
      return t(($) => $.pricingv2["Right Price"]);
    case "Out of Stock":
      return t(($) => $.pricingv2["Out of Stock"]);
    case "Slow Movers":
      return t(($) => $.pricingv2["Slow Movers"]);
    case "Profiling":
      return t(($) => $.pricingv2["Profiling"]);
    default:
      return label ?? "";
  }
};

const PricingPlanCell = (props: { original: DashboardStats; value: ReactNode }) => {
  const { t } = useTranslation(["pricingv2"]);
  const hasPriceDebugModule = useModuleEnabled("PRICE_DEBUG");
  const params = {
    plan_type: props.original.plan_type || "",
    is_auto: props.original.is_auto || true,
    current_program: { template_type: props.original.current_program__template_type || "" },
  };

  const planStrategy = getPlanType(params);
  const image = `${URL_PREFIX}/api/productimage/?pricingplan=${props.original.id}`;

  return (
    <TrellisLink
      to={`/user/pricing/plan/${props.original.id}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        textDecoration: "none",
        fontWeight: "500",
        fontSize: "1.5rem",
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex" }}>
        <div
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            flexShrink: 0,
            width: 50,
            height: 50,
            marginRight: "10px",
            background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
          }}
          draggable="false"
        />
        <Row>
          <Col className="text-start" xs={12}>
            {props.value}
          </Col>
          <Col className="text-start" xs={12}>
            {hasPriceDebugModule &&
              props.original.status_reason !== undefined &&
              props.original.status_reason === "Price Mismatch" && (
                <span className="pe-2" style={{ color: trellisPalette[17] }}>
                  <FaExclamationTriangle />
                </span>
              )}

            {planStrategy !== "Dynamic Price" && (
              <Badge
                className={`badge bg-trellis-lightgrey text-end me-3`}
                style={{
                  borderRadius: "5px",
                }}
              >
                <span>{getPlanTypeLabel(t, planStrategy)}</span>
              </Badge>
            )}
            {planStrategy === "Dynamic Price" && (
              <>
                <Badge
                  className={`badge bg-trellis-purple text-end me-3`}
                  style={{
                    borderRadius: "5px",
                  }}
                >
                  {props.original.enroll_date &&
                    (props.original.configured_slider_value ?? 0) >= 0 &&
                    (props.original.configured_slider_value ?? 0) <= 0 && (
                      <span>{t(($) => $.pricingv2.strategy["Balanced"])}</span>
                    )}
                  {props.original.enroll_date && (props.original.configured_slider_value ?? 0) < 0 && (
                    <span>{t(($) => $.pricingv2.strategy["Volume"])}</span>
                  )}
                  {props.original.enroll_date && (props.original.configured_slider_value ?? 0) > 0 && (
                    <span>{t(($) => $.pricingv2.strategy["Margin"])}</span>
                  )}
                  {!props.original.enroll_date && <span>{t(($) => $.pricingv2["n/a"])}</span>}
                </Badge>
                <Badge
                  className={`badge bg-trellis-mauve text-end me-3`}
                  style={{
                    borderRadius: "5px",
                  }}
                >
                  {props.original.bucket ? translateSegmentText(t, SEGMENT_TEXT[props.original.bucket]) : ""}
                </Badge>
              </>
            )}

            {planStrategy === "Rules Engine" && (
              <Badge
                className={`badge bg-trellis-fuchsia text-end me-3`}
                style={{
                  borderRadius: "5px",
                }}
              >
                <span>{getTemplateTypeLabel(t, props.original.current_program__template_type)}</span>
              </Badge>
            )}

            {props.original.bucket && <ForecastBadge bucket={props.original.bucket} />}

            {props.original.tags?.map((tag, index) => (
              <Badge
                className="bg-trellis-mauve"
                style={{
                  marginRight: "0.5rem",
                  cursor: "pointer",
                }}
                pill
                key={`${tag}-${index}`}
              >
                {tag}
              </Badge>
            ))}
          </Col>
        </Row>
      </div>
    </TrellisLink>
  );
};

export interface DashboardStats {
  asp: number;

  inc_cashflow: number;
  inc_contribution: number;
  inc_price: number;
  inc_units: number;
  name: string;
  price_decreased: number;
  price_decreased_avg: number;
  price_increased: number;
  price_increased_avg: number;
  reference_cr: number;
  reference_price: number;
  target_sales: number;
  target_units: number;
  total_sales: number;
  total_units: number;
  page_views: number;
  value: number;

  plan_name?: string;
  id?: number;
  plan_id?: number;
  status?: "enabled" | "disabled";
  status_reason?: string;
  plan_type?: string;
  is_auto?: boolean;
  current_program?: { template_type: string };
  current_program__template_type?: string | null;
  enroll_date?: string;
  configured_slider_value?: number;
  bucket?: string;
  category_id?: number;
  override_supply?: boolean;
  tags?: string[];

  asp_to_budgeted_price?: number;
  sales_percentage_diff?: number;
  forecasted_units?: number;
  actual_units?: number;
  forecasted_sales?: number;
  actual_sales?: number;
  best_price?: number;

  units_current_period?: number;
  units_prior_period?: number;
  units_period_delta?: number;
  units_period_percent?: number | null;
  sales_current_period?: number;
  sales_prior_period?: number;
  sales_period_delta?: number;
  sales_period_percent?: number | null;
}

export interface PlanStrategyPlanList {
  volume: number[];
  balanced: number[];
  margin: number[];
  constrained: number[];
  override_supply: number[];
}

export interface ScatterStats {
  scatterData: { x: number; y: number; name: string; planId: number }[];
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface Limits {
  incUnitsMin: number;
  incUnitsMax: number;
  incContributionMin: number;
  incContributionMax: number;
}

interface Props {
  pricingPlans: DashboardStats[];
  fromV4?: boolean;
  fromForecast?: boolean;
}

const ExpandedContent = ({ row }: { row: { original: { plan_id: number } } }) => {
  return (
    <div style={{ backgroundColor: "#efefef" }}>
      <PricingPlanCard planId={row.original.plan_id} />
    </div>
  );
};

const PricingPlanTable = ({ pricingPlans, fromV4 = false, fromForecast }: Props) => {
  const { t } = useTranslation(["pricingv2"]);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const [selectedPlans, setSelectedPlans] = useState<number[]>([]);
  const TABLE_ID = "pricingPlanTable";
  const tableRef = useRef(null);
  const quickConfigModal = useModal(QuickPlanConfigModal);

  const userCanUpdatePricingPlans = useSimpleCan("update", "PricingPlan");

  const togglePlanStatus = useBulkUpdatePricingPlanStatus();
  const selectedPlanObjects = pricingPlans.filter((plan) => plan.id !== undefined && selectedPlans.includes(plan.id));
  const disablePause = selectedPlans.length === 0 || selectedPlanObjects.every((plan) => plan.status === "disabled");
  const disableResume = selectedPlans.length === 0 || selectedPlanObjects.every((plan) => plan.status === "enabled");

  const { PLAN_SEGMENT_FILTER, PLAN_TYPE_FILTER, RULES_FILTER, PLAN_STRATEGY_FILTER } = usePricingLifecycleFilters();
  const ADDITIONAL_FILTERS = [PLAN_SEGMENT_FILTER, PLAN_TYPE_FILTER, RULES_FILTER, PLAN_STRATEGY_FILTER];

  const filterProps = useCustomTableFilters(TABLE_ID);

  const fmtPrice = (price: number | undefined | null) => {
    return price ?
        formatCurrency(price, {
          marketPlace: marketPlace.marketPlace,
          compact: true,
        })
      : t(($) => $.pricingv2["N/A"]);
  };

  const fmtDate = (date: MomentInput | null | undefined) => {
    return date ? moment(date).format("MM/DD/YYYY") : t(($) => $.pricingv2["N/A"]);
  };

  const columns: ColumnSpec[] = [
    {
      key: "status",
      options: {
        editCell: true,
        format: "toggle",
        columnTitle: t(($) => $.pricingv2["Active"]),
        onValue: "enabled",
        offValue: "disabled",
        toolTip: t(($) => $.pricingv2["The current status of a pricing plan"]),
        disabled: !userCanUpdatePricingPlans,
      },
    },
  ];

  if (!fromV4 && !fromForecast) {
    const extraColumns: ColumnSpec[] = [
      {
        key: "product_count",
        options: {
          columnTitle: t(($) => $.pricingv2["Product Count"]),
          format: "number",
          toolTip: t(($) => $.pricingv2["The number of products in a pricing plan"]),
          checked: false,
        },
      },
      {
        key: "enroll_date",
        options: {
          columnTitle: t(($) => $.pricingv2["Enroll Date"]),
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: fmtDate as any,
          checked: false,
        },
      },
      {
        key: "asp",
        options: {
          checked: true,
          overrideCompare: true,
          overrideCompareAccessor: (d) => {
            return {
              total_units: d.total_units,
              sales: d.total_sales,
              total_units_prev: d.total_units_prev,
              sales_prev: d.total_sales_prev,
            };
          },
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          overrideCompareFormatter: (v: any) => {
            const avgPrice = v.sales > 0 ? v.sales / v.total_units : 0;
            const preAvgPrice = v.total_units_prev > 0 ? v.sales_prev / v.total_units_prev : 0;
            const diff = (avgPrice - preAvgPrice) / preAvgPrice;
            let color = "#555";

            if (diff >= 0.03) {
              color = trellisPalette[12];
            } else if (diff <= -0.03) {
              color = trellisPalette[17];
            }
            return (
              <span style={{ color: "#555" }}>
                <span style={{ color: color }}>
                  {formatPercent(diff, {
                    addPlus: true,
                  })}
                </span>
              </span> // oxlint-disable-next-line @typescript-eslint/no-explicit-any
            ) as any;
          },
        },
      },
      {
        key: "best_price",
        compareKey: "price_delta",
        options: {
          columnTitle: t(($) => $.pricingv2["Price"]),
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: formatCurrency as any,
          format: "currency",
          toolTip: t(($) => $.pricingv2["The current price of a product"]),
          checked: false,
        },
      },
      {
        key: "sales",
        compareKey: "sales_percent",
        options: { checked: true },
      },
      {
        key: "units",
        compareKey: "units_percent",
        options: { checked: true },
      },
      {
        key: "ad_spend",
        compareKey: "ad_spend_percent",
        options: { checked: true },
      },
      {
        key: "profit",
        compareKey: "profit_percent",
        options: { columnTitle: t(($) => $.pricingv2["GCD"]), checked: true },
      },
      {
        key: "page_views",
        compareKey: "page_views_delta",
        options: { checked: false },
      },
      {
        key: "profit_per_page_view",
        compareKey: "profit_per_page_view_delta",
        options: {
          columnTitle: t(($) => $.pricingv2["Profit/Page View"]),
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: fmtPrice as any,
          checked: false,
        },
      },
      {
        key: "total_conv_rate",
        compareKey: "total_conv_rate_delta",
        options: {
          checked: true,
          columnTitle: t(($) => $.pricingv2["Conv/Rate"]),
          metric: {
            id: "total_conv_rate",
            format: "percent",
          },
        },
      },
      {
        key: "bsr",
        compareKey: "bsr_delta",
        options: {
          checked: true,
          overrideCompare: true,
          overrideCompareAccessor: (d) => {
            return {
              bsr_rank: d.bsr_rank,
              bsr_rank_prev: d.bsr_rank_prev,
            };
          },
          overrideCompareFormatter: (v) => {
            return (
              <span style={{ color: "#555" }}>
                <span>{formatNumber(v.bsr_rank_prev, { decimalPlaces: 2 })}</span>
              </span>
            );
          },
        },
      },
    ];
    columns.push(...extraColumns);
  } else if (fromV4) {
    const extraColumns: ColumnSpec[] = [
      {
        key: "enroll_date",
        options: {
          columnTitle: t(($) => $.pricingv2["Enroll Date"]),
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: fmtDate as any,
          checked: false,
        },
      },
      {
        key: "asp",
        options: {
          checked: false,
          overrideCompare: false,
        },
      },
      {
        key: "best_price",
        compareKey: null,
        options: {
          columnTitle: t(($) => $.pricingv2["Price"]),
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: formatCurrency as any,
          format: "currency",
          toolTip: t(($) => $.pricingv2["The current price of a product"]),
          checked: true,
        },
      },

      {
        key: "reference_price",
        compareKey: null,
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        options: { checked: true, formatter: formatCurrency as any, columnTitle: t(($) => $.pricingv2["Ref. Price"]) },
      },
      {
        key: "sales",
        compareKey: null,
        options: { checked: true },
      },
      {
        key: "target_sales",
        compareKey: null,
        options: { checked: false, columnTitle: t(($) => $.pricingv2["Expected Sales"]) },
      },
      {
        key: "inc_cashflow",
        compareKey: null,
        options: { checked: true, columnTitle: t(($) => $.pricingv2["Sales Above Expected"]) },
      },
      {
        key: "units",
        compareKey: null,
        options: { checked: true },
      },
      {
        key: "target_units",
        compareKey: null,
        options: { checked: false, columnTitle: t(($) => $.pricingv2["Expected Units"]) },
      },
      {
        key: "inc_units",
        compareKey: null,
        options: { checked: true, columnTitle: t(($) => $.pricingv2["Units Above Expected"]) },
      },
      {
        key: "page_views",
        compareKey: null,
        options: { checked: false },
      },
      {
        key: "reference_cr",
        compareKey: null,
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        options: { checked: true, formatter: formatPercent as any, columnTitle: t(($) => $.pricingv2["Ref. CR"]) },
      },
      {
        key: "inc_contribution",
        compareKey: null,
        options: {
          checked: true,
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: formatCurrency as any,
          columnTitle: t(($) => $.pricingv2["Incremental GCD"]),
        },
      },
    ];
    columns.push(...extraColumns);
  } else {
    const extraColumns: ColumnSpec[] = [
      {
        key: "best_price",
        compareKey: null,
        options: {
          columnTitle: t(($) => $.pricingv2["Price"]),
          // oxlint-disable-next-line @typescript-eslint/no-explicit-any
          formatter: formatCurrency as any,
          format: "currency",
          toolTip: t(($) => $.pricingv2["The current price of a product"]),
          checked: true,
        },
      },

      {
        key: "reference_price",
        compareKey: null,
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        options: { checked: true, formatter: formatCurrency as any, columnTitle: t(($) => $.pricingv2["Ref. Price"]) },
      },
      {
        key: "forecasted_sales",
        compareKey: null,
        options: { checked: true, columnTitle: t(($) => $.pricingv2["For. Sales"]), formatter: formatCurrency },
      },
      {
        key: "actual_sales",
        compareKey: null,
        options: { checked: true, columnTitle: t(($) => $.pricingv2["Actual Sales"]), formatter: formatCurrency },
      },
      {
        key: "forecasted_units",
        compareKey: null,
        options: { checked: true, columnTitle: t(($) => $.pricingv2["For. Units"]), formatter: formatNumber },
      },
      {
        key: "actual_units",
        compareKey: null,
        options: { checked: true, columnTitle: t(($) => $.pricingv2["Actual Units"]), formatter: formatNumber },
      },
      {
        key: "asp_to_budgeted_price",
        compareKey: null,
        options: {
          checked: true,
          columnTitle: t(($) => $.pricingv2["($) Deviation"]),
          formatter: formatPercent,
        },
      },
      {
        key: "sales_percentage_diff",
        compareKey: null,
        options: { checked: true, columnTitle: t(($) => $.pricingv2["For. Deviation"]), formatter: formatPercent },
      },
    ];
    columns.push(...extraColumns);
  }

  const actionColumns: ColumnSpec[] = [
    {
      custom: true,
      id: "actions",
      accessor: (v) => v,
      name: t(($) => $.pricingv2["Actions"]),
      Header: <MetricLabel label={t(($) => $.pricingv2["Actions"])} />,
      sortable: false,
      headerClassName: "text-end",
      className: "text-end",
      headerStyle: {
        flex: "unset",
        flexGrow: "unset",
        flexShrink: "unset",
        flexBasis: "unset",
      },
      style: {
        height: "60px",
        flex: "unset",
        flexGrow: "unset",
        flexShrink: "unset",
        flexBasis: "unset",
      },
      Cell: (props) => {
        return (
          <Button
            variant="icon"
            onPress={() => {
              void quickConfigModal.show({
                planId: props.value.id,
              });
            }}
          >
            <MdEdit size={"1.5em"} color={trellisPalette[3]} />
          </Button>
        );
      },
    },
  ];

  if (!fromV4 && !fromForecast && userCanUpdatePricingPlans) {
    columns.push(...actionColumns);
  }

  return (
    <BulkActionsTable<DashboardStats>
      handleBulkChanges={(changes) => {
        const entries = Object.entries(changes);
        const enable = entries.filter((entry) => entry[1].status === "enabled").map((entry) => Number(entry[0]));
        const disable = entries.filter((entry) => entry[1].status === "disabled").map((entry) => Number(entry[0]));
        togglePlanStatus.mutate({
          enable,
          disable,
        });
      }}
      tableId={TABLE_ID}
      tableRef={tableRef}
      entityType={"pricingPlan"}
      titleCol={{
        id: "pricingplan",
        accessor: "plan_name",
        width: 350,
        name: t(($) => $.pricingv2["Pricing Plan"]),
        Header: <MetricLabel label={t(($) => $.pricingv2["Pricing Plan"])} />,
        Cell: (props) => {
          return <PricingPlanCell {...props} />;
        },
      }}
      columnSpecs={columns}
      defaultPageSize={Math.min(pricingPlans.length, 5)}
      minRows={Math.min(pricingPlans.length, 5)}
      data={pricingPlans}
      selectionKey="plan_id"
      selected={selectedPlans}
      // oxlint-disable-next-line @typescript-eslint/no-explicit-any
      setSelected={setSelectedPlans as any}
      isExpandable
      isSelectable={userCanUpdatePricingPlans}
      ExpandedContent={({ row }) => {
        return <ExpandedContent row={row!} />;
      }}
      bulkActionOptions={[
        {
          label: t(($) => $.pricingv2["Pause Pricing Plans"], { count: selectedPlans.length }),
          action: () => {
            togglePlanStatus.mutate(
              {
                disable: selectedPlans,
              },
              {
                onSuccess: () => {
                  setSelectedPlans([]);
                },
              },
            );
          },
          confirmationMessage: (
            <p>
              {t(($) => $.pricingv2["Are you sure you want to pause the selected pricing plans?"], {
                count: selectedPlans.length,
              })}
            </p>
          ),
          disabled: disablePause,
        },
        {
          label: t(($) => $.pricingv2["Enable Pricing Plans"], { count: selectedPlans.length }),
          action: () => {
            togglePlanStatus.mutate(
              {
                enable: selectedPlans,
              },
              {
                onSuccess: () => {
                  setSelectedPlans([]);
                },
              },
            );
          },
          confirmationMessage: (
            <p>
              {t(($) => $.pricingv2["Are you sure you want to unpause the selected pricing plans?"], {
                count: selectedPlans.length,
              })}
            </p>
          ),
          disabled: disableResume,
        },
      ]}
      getSearchCriteria={(row) => {
        const { plan_name } = row as { plan_name: string };
        return `${plan_name}`;
      }}
      hideBulkActionsButton={!userCanUpdatePricingPlans}
      additionalFilterOptions={ADDITIONAL_FILTERS}
      tableRowDescription={t(($) => $.pricingv2["Pricing Plans"])}
      filterWidget={true}
      {...filterProps}
      excludedFilterColumns={["plan_name"]}
      hideFilterToggleIcon={true}
      hideResetFiltersIcon={false}
      extendMetrics={false}
    />
  );
};

export default PricingPlanTable;
